import { getLayerProps } from '@/utils/layer-kit';
import {
    LayerType,
    MaterialIdsType,
    StretchOptionType,
    VectorType,
    type ILayerProps,
    type IVectorLayerProps,
    type IVectorMaterialInfo,
} from '@/version-types';

/**
 * 将旧的统一圆角字段转换为四角圆角结构。
 *
 * @param layerData - 当前矢量图层数据。
 * @returns 可直接用于矩形路径构建的四角圆角数据。
 */
export const normalizeBorderRadius = (layerData: IVectorLayerProps) => {
    const { borderRadius, radius } = layerData;

    if (borderRadius) return borderRadius;

    return {
        leftTop: radius,
        rightTop: radius,
        leftBottom: radius,
        rightBottom: radius,
    };
};

/**
 * 收集目标图层及其子树中的所有 Vector Layer。
 *
 * @param layer - 待遍历的根图层。
 * @returns 根图层或其后代中的 Vector Layer 列表。
 */
export const collectVectorLayers = (layer: ILayerProps): ILayerProps[] => {
    const vectorLayers: ILayerProps[] = [];

    /**
     * 深度优先遍历当前图层子树。
     *
     * @param item - 当前访问的图层。
     * @returns 无返回值。
     */
    const visit = (item: ILayerProps): void => {
        if (item.layerType === LayerType.VECTOR_LAYER) vectorLayers.push(item);
        if (item.children?.length) item.children.forEach(visit);
    };

    visit(layer);

    return vectorLayers;
};

/**
 * 判断 VectorType 是否依赖外部 SVG 素材。
 *
 * @param vectorType - 当前矢量图形类型。
 * @returns Polygon / Graph 返回 true，其余类型返回 false。
 */
export const requiresSource = (vectorType: VectorType): boolean => {
    return vectorType === VectorType.Polygon || vectorType === VectorType.Graph;
};

/**
 * 生成默认矢量图层 layerData
 * @param override - 覆盖默认值
 * @returns - 矢量图层 layerData
 */
export const createDefaultVectorLayerData = (override: Partial<IVectorLayerProps> = {}) => {
    const DEFAULT_LAYER_DATA: IVectorLayerProps = {
        url: '',
        colors: [],
        naturalWidth: 800,
        naturalHeight: 800,

        vectorType: VectorType.Rect,
        fill: '',
        fillGradient: null,
        stroke: '',
        strokeGradient: null,
        strokeWidth: 0,
        strokeDasharray: '',
        strokeLinecap: '',
        strokeHidden: false,
        radius: 0,
        pickerIndex: null,
        blur: 0,
        stretchOption: StretchOptionType.fill,
        imageTransform: {
            offsetX: 0,
            offsetY: 0,
            rotate: 0,
            scale: 1,
            flipH: false,
            flipV: false,
            widthRatio: 1,
            heightRatio: 1,
        },
        effect: {
            shadow: [],
            reflection: [],
        },
    };

    const layerData = {
        ...DEFAULT_LAYER_DATA,
        ...override,
    };

    const borderRadius = normalizeBorderRadius(layerData);
    layerData.borderRadius = borderRadius;

    return layerData;
};

type CreationOptions = Pick<ILayerProps, 'width' | 'height' | 'left' | 'top'> & {
    materialId?: string;
    data: IVectorLayerProps;
    lockRatio: boolean;
};

/**
 * 根据部分选项（覆盖数据）创建矢量图层
 *
 * @param options - 创建配置选项
 * @returns - 矢量图层
 */
export const createVectorLayer = (options: CreationOptions) => {
    const { width, height, left, top, materialId = '', data, lockRatio } = options;
    if (data.naturalWidth && data.strokeWidth) {
        const ratio = width / data.naturalWidth;
        data.strokeWidth = data.strokeWidth * ratio;
        if (data.strokeWidth < 1) {
            data.strokeWidth = 1;
        }
    }
    console.log('===width,height,data', width, height, data);
    const layer = getLayerProps({
        layerType: LayerType.VECTOR_LAYER,
        data,
    });

    layer.width = width;
    layer.height = height;
    layer.left = left;
    layer.top = top;
    layer.__proactiveWidth = width;
    layer.__proactiveHeight = height;
    layer.extraInfo = {
        ...layer.extraInfo,
        lockRatio,
    };

    if (materialId) {
        const nextMaterialIds = layer.materialIds || {};
        nextMaterialIds[MaterialIdsType.VECTOR_MATERIAL] = [materialId];
        layer.materialIds = nextMaterialIds;
    }

    return layer;
};

/**
 * 获取空图层数据
 */
export function getEmptyVectorLayerProps() {
    return getLayerProps({
        layerType: LayerType.VECTOR_LAYER,
        data: {
            url: '',
            colors: [],
            naturalWidth: 800,
            naturalHeight: 800,

            // maskURL: '',
            // maskType: MaskType.none,

            vectorType: '',
            fill: '',
            fillGradient: null,
            stroke: '',
            strokeGradient: null,
            strokeWidth: 0,
            strokeDasharray: '',
            strokeLinecap: '',
            strokeHidden: false,
            radius: 0,
            pickerIndex: null,
            blur: 0,
            stretchOption: StretchOptionType.fill,
            imageTransform: {
                offsetX: 0,
                offsetY: 0,
                rotate: 0,
                scale: 1,
                flipH: false,
                flipV: false,
                widthRatio: 1,
                heightRatio: 1,
            },
            effect: {
                shadow: [],
                reflection: [],
            },
            borderRadius: {
                leftTop: 0,
                rightTop: 0,
                leftBottom: 0,
                rightBottom: 0,
            },
        },
    });
}

/**
 * 获取矢量图层数据
 *
 * @param url - 矢量图链接地址
 * @param colors - 颜色列表
 */
export function getVectorLayerProps(
    {
        url,
        left,
        top,
        width,
        height,
        materialId,
    }: {
        url: string;
        left: number;
        top: number;
        width: number;
        height: number;
        materialId?: string;
    },
    info: IVectorMaterialInfo,
) {
    const layer = getEmptyVectorLayerProps();
    layer.width = width;
    layer.height = height;
    layer.left = left;
    layer.top = top;
    if (materialId) {
        layer.materialIds = layer.materialIds || {};
        layer.materialIds[MaterialIdsType.VECTOR_MATERIAL] = [materialId];
    }
    (layer.data as IVectorLayerProps).url = url;
    layer.data = { ...(layer.data as IVectorLayerProps), ...info };

    if (!info.borderRadius) {
        const { radius } = layer.data;
        layer.data.borderRadius = {
            leftTop: radius,
            rightTop: radius,
            leftBottom: radius,
            rightBottom: radius,
        };
    }
    return layer;
}
